// UI Primitives - Centralized design tokens for glassmorphic aesthetic
// Following ui-skills guidelines for accessibility and mobile-first design

// ============================================================================
// UTILITY: Class name combiner (replaces clsx + tailwind-merge for simplicity)
// ============================================================================

export const cx = (...classes: (string | boolean | undefined | null)[]): string =>
  classes.filter(Boolean).join(' ')

// ============================================================================
// LAYOUT PRIMITIVES - Mobile-first with safe areas
// ============================================================================

export const layout = {
  // Use h-dvh instead of h-screen for proper mobile viewport
  page: 'h-dvh bg-[#07070A] text-white selection:bg-white/20 selection:text-white',

  // Safe area padding for iOS notch and home bar
  safeTop: 'pt-[env(safe-area-inset-top,0px)]',
  safeBottom: 'pb-[env(safe-area-inset-bottom,0px)]',
  safeLeft: 'pl-[env(safe-area-inset-left,0px)]',
  safeRight: 'pr-[env(safe-area-inset-right,0px)]',
  safeAll: 'pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]',

  // Centered backdrop with gradient
  backdrop: 'h-dvh flex items-center justify-center p-4 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.08),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(99,102,241,0.10),_transparent_60%)]',

  // Kiosk shell - responsive sizing
  // Mobile: full height, Desktop: fixed max
  kioskShell: cx(
    'w-full rounded-[28px] border border-white/10 bg-white/[0.03] shadow-[0_30px_80px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col',
    // Mobile: use available height with safe areas
    'h-[calc(100dvh-2rem)]',
    // Tablet/Desktop: constrained max width and height
    'sm:max-w-md sm:h-[min(750px,calc(100dvh-4rem))]',
    'lg:max-w-lg'
  ),

  // Screen content wrapper with responsive padding
  screenContent: cx(
    'flex flex-col h-full',
    'px-4 py-5',      // Mobile: tighter padding
    'sm:px-6 sm:py-8' // Tablet+: more breathing room
  ),
}

// ============================================================================
// UI COMPONENTS
// ============================================================================

export const ui = {
  // Header bar
  header: 'flex items-center justify-between px-4 sm:px-5 py-3 border-b border-white/10 bg-black/20 backdrop-blur-sm',
  subheaderMono: 'text-white/50 text-xs font-mono',

  // Cards - glassmorphic style
  card: 'rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm',
  cardSoft: 'rounded-2xl border border-white/10 bg-white/[0.03]',
  cardHover: 'hover:border-white/20 hover:bg-white/[0.05] transition-all duration-200',
  cardInteractive: cx(
    'rounded-2xl border border-white/10 bg-white/[0.03]',
    'hover:border-white/20 hover:bg-white/[0.05]',
    'active:scale-[0.98]',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/20'
  ),

  // Pills and badges
  pill: 'inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.04]',
  badge: 'px-3 py-1 rounded-full text-xs font-medium border border-white/10 bg-white/[0.04]',

  // Inputs - with proper focus states and touch targets
  input: cx(
    'w-full bg-black/20 border-2 border-white/10 rounded-xl',
    'px-4 py-3 min-h-[48px]', // ≥48px touch target
    'text-white placeholder:text-white/30',
    'focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/20',
    'transition-all'
  ),
  textarea: cx(
    'w-full bg-black/20 border-2 border-white/10 rounded-xl',
    'px-4 py-3 min-h-[48px]',
    'text-white placeholder:text-white/30',
    'focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/20',
    'resize-none transition-all'
  ),

  // Buttons - all with ≥48px touch targets
  btnBase: cx(
    'inline-flex items-center justify-center gap-2 rounded-xl font-medium',
    'min-h-[48px] px-4', // ≥48px touch target
    'transition-all active:scale-[0.98]',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'focus:outline-none focus-visible:ring-4 focus-visible:ring-white/15'
  ),
  btnGhost: 'bg-white/[0.04] hover:bg-white/[0.08] text-white/80 border border-white/10',
  btnQuiet: 'bg-transparent hover:bg-white/[0.04] text-white/70 border border-transparent',
  btnPrimary: 'hover:scale-[1.01] active:scale-[0.98]',
  btnDanger: 'bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-500/25',

  // Back button - specific styling with touch target
  backBtn: cx(
    'inline-flex items-center gap-2 min-h-[44px] min-w-[44px] -ml-2 px-2',
    'text-white/55 hover:text-white rounded-lg',
    'transition-colors',
    'focus:outline-none focus-visible:ring-4 focus-visible:ring-white/15'
  ),

  // Selection cards (crypto/network selection) - with proper touch targets
  selectionCard: cx(
    'w-full p-4 sm:p-5 rounded-2xl border-2 border-white/10',
    'min-h-[72px]', // Comfortable touch target
    'hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.05]',
    'transition-all duration-200 hover:scale-[1.01] active:scale-[0.98]',
    'focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/20'
  ),

  // Error states
  error: 'bg-red-500/15 border border-red-500/30 rounded-xl p-3 text-red-300 text-sm text-center',

  // Divider
  divider: 'border-t border-white/10',
  dividerVertical: 'border-l border-white/10',
}

// ============================================================================
// TYPOGRAPHY - with text-balance for headings
// ============================================================================

export const text = {
  // Colors
  primary: 'text-white',
  secondary: 'text-white/55',
  tertiary: 'text-white/45',
  disabled: 'text-white/30',
  subtle: 'text-white/35',

  // Headings - with text-balance for proper wrapping
  h1: 'text-3xl font-bold text-white tracking-tight text-balance',
  h2: 'text-2xl font-bold text-white tracking-tight text-balance',
  h3: 'text-xl font-semibold text-white tracking-tight text-balance',

  // Body - with text-pretty for optimal line breaks
  body: 'text-base text-white/80 text-pretty',
  bodySmall: 'text-sm text-white/55 text-pretty',

  // Mono for codes/IDs
  mono: 'font-mono',
  monoSmall: 'font-mono text-sm',

  // Numeric (tabular figures)
  numeric: 'tabular-nums',
}

// ============================================================================
// STATUS STYLES
// ============================================================================

export const statusStyles = {
  pending: 'bg-yellow-500/15 text-yellow-200 border border-yellow-500/25',
  approved: 'bg-indigo-500/15 text-indigo-200 border border-indigo-500/25',
  completed: 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/25',
  rejected: 'bg-red-500/15 text-red-200 border border-red-500/25',
  cancelled: 'bg-white/10 text-white/50 border border-white/15',
}

// ============================================================================
// VALIDATION INDICATORS
// ============================================================================

export const validationStyles = {
  valid: 'bg-emerald-400',
  invalid: 'bg-rose-400',
  empty: 'bg-white/20',
}

// ============================================================================
// LOADING SPINNER
// ============================================================================

export const spinner = {
  base: 'rounded-full animate-spin',
  default: 'border-4 border-white/15 border-t-indigo-400',
  small: 'size-5 border-2 border-white/15 border-t-indigo-400',
  medium: 'size-8 border-2 border-white/15 border-t-indigo-400',
  large: 'size-20 border-4 border-white/15 border-t-indigo-400',
}

// ============================================================================
// RATE PILL (for locked rate timer)
// ============================================================================

export const ratePill = {
  base: 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm min-h-[36px]',
  active: 'bg-yellow-500/10 text-yellow-200 border border-yellow-500/20',
  expired: 'bg-red-500/10 text-red-200 border border-red-500/20',
}

// ============================================================================
// RESPONSIVE BREAKPOINT HELPERS
// ============================================================================

export const responsive = {
  // Hide on mobile, show on larger screens
  hideOnMobile: 'hidden sm:block',
  // Show on mobile, hide on larger screens
  showOnMobile: 'sm:hidden',
  // Stack on mobile, row on larger screens
  stackToRow: 'flex flex-col sm:flex-row',
}

// ============================================================================
// Z-INDEX SCALE (from ui-skills)
// ============================================================================

export const zIndex = {
  base: 'z-0',
  dropdown: 'z-10',
  sticky: 'z-20',
  modal: 'z-30',
  toast: 'z-40',
  tooltip: 'z-50',
}
